// linkWheelLoadError.js — QA-1250.
// A LinkWheel codeblock that references a DELETED / unavailable wheel must NEVER
// surface the raw axios string ("Request failed with status code 404") as a toast
// in the builder. The View-Wheel page already degrades this gracefully
// (src/pages/ViewWheel/index.jsx: 404/410 -> friendly "This wheel isn't available"
// state, never `error.message`). This mirrors that contract for the builder's
// linked-wheel fetch so the two surfaces behave consistently.

export const GENERIC_LINK_WHEEL_LOAD_ERROR =
  "We couldn't load this linked wheel. Please try again.";

// stgport-embed-url-error-handling-qa1250-to-staging: the silent 404/410 omit
// below is correct for a SAVED block whose linked wheel was later deleted
// (matches View-Wheel's graceful degrade — nothing the user just did caused
// it). It gives no feedback at all, though, when a user just pasted a
// wrong/cross-host URL into the Embed URL field: they need to be told the
// paste didn't work, not left staring at a stuck skeleton.
export const EMBED_URL_NOT_FOUND_ERROR =
  "This link cannot be embedded — that wheel couldn't be found. Check the URL and try again.";

// Decide what the user should see when a linked-wheel fetch fails.
//   404 / 410  -> the wheel is gone (or never existed): omit it silently
//                 (return null -> no toast) UNLESS the fetch was triggered by
//                 the user's own Embed URL paste (isEmbedAction), in which
//                 case surface EMBED_URL_NOT_FOUND_ERROR.
//   anything else (5xx / network) -> a HUMAN message, NEVER the axios
//                 `error.message`.
// The one thing it must never do is return the raw axios message.
export function resolveLinkWheelLoadError(error, { isEmbedAction = false } = {}) {
  const status = error?.response?.status;
  if (status === 404 || status === 410) {
    return isEmbedAction ? EMBED_URL_NOT_FOUND_ERROR : null;
  }
  return GENERIC_LINK_WHEEL_LOAD_ERROR;
}

// hw-0914-dev-embed-url-accepts-nonexistent-wheel-id: the Embed URL tab's
// paste-and-Confirm validates the id BEFORE saving it (see
// LinkWheelSettings.handleEmbedWheel). Unlike a silent block mount, this is a
// direct user action awaiting feedback, so a wheel that fails to resolve
// (404/410, or any other error) always gets ONE clear message — never the
// silent omit above, and never the raw axios string.
export const EMBED_LINK_WHEEL_LOAD_ERROR =
  "This link cannot be embedded — that wheel couldn't be found.";

export function resolveEmbedLinkWheelError(error) {
  const status = error?.response?.status;
  if (status === 404 || status === 410) return EMBED_LINK_WHEEL_LOAD_ERROR;
  return GENERIC_LINK_WHEEL_LOAD_ERROR;
}
